/*
  Promo popup — the storefront side of sections/promo-popup.liquid.

  Who sees it is decided by PromoPopupRules below, which is pure (no DOM, no
  storage) so scripts/test-promo-popup.mjs can run the real code. The geo
  verdict comes from the resolver in layout/theme.liquid: we await
  window.userGeoReady rather than listening for geoReady, because geoReady may
  already have fired by the time this runs.

  Green targets fail closed, like prices: a dead worker leaves isGreenState
  false, and false means no popup.

  When it opens is earned, not timed. Each visit keeps an interest score
  across pages (POINTS below): a product page, exploring it (color, gallery),
  entering an area in its materials calculator,
  reading halfway down a page, active time, another page. Once the score
  reaches the section's threshold the popup waits for a calm moment — a pause
  after scrolling, or the mouse leaving the window — and never opens over a
  menu, the cart, another dialog or a field being typed in.

  promoDebug() in the console says where a visitor stands and why.
*/
(function () {
  // Every popup section on a page includes this file; run it once.
  if (window.PromoPopupRules) return;

  // <promo-popup-rules>
  var PromoPopupRules = {
    // geo: { green, greenB2B } or null when the resolver gave no answer.
    matchesGeo: function (target, geo) {
      if (target === 'all') return true;
      var green = Boolean(geo && geo.green);
      var greenB2B = Boolean(geo && geo.greenB2B);
      if (target === 'green') return green;
      if (target === 'green_b2b') return greenB2B;
      if (target === 'green_any') return green || greenB2B;
      if (target === 'red') return !green;
      return false; // an unknown target never opens
    },

    matchesDevice: function (device, isMobile) {
      if (device === 'mobile') return isMobile;
      if (device === 'desktop') return !isMobile;
      return true;
    },

    // Everything that can be decided before the geo answer arrives: why this
    // visitor will not get the popup, or null when they may.
    // state: { dismissedAt, subscribed, isMobile, now }
    whyNot: function (cfg, state) {
      if (!cfg.enabled) return 'disabled in the theme editor';
      if (cfg.hideForCustomers && cfg.isCustomer) return 'hidden for logged-in customers';
      if (cfg.hideForB2B && cfg.isB2B) return 'hidden for B2B customers';
      if (cfg.mode === 'newsletter' && state.subscribed) return 'already subscribed (cm-newsletter-subscribed)';
      if (!PromoPopupRules.matchesDevice(cfg.device, state.isMobile)) return 'not shown on this device';
      if (state.dismissedAt) {
        var wait = Number(cfg.reshowMs) || 0;
        if (wait > 0 && state.now - state.dismissedAt < wait) {
          return 'closed recently, back in ' + Math.ceil((wait - (state.now - state.dismissedAt)) / 60000) + ' min';
        }
      }
      return null;
    },

    eligible: function (cfg, state) {
      return PromoPopupRules.whyNot(cfg, state) === null;
    },

    // ---- interest score ----
    // A visit: { score, points: { kind: count }, pages, active, last }, kept
    // across pages. It ends after VISIT_GAP_MS without activity, like an
    // analytics session.
    POINTS: { product: 2, calculator: 2, page: 1, explore: 1, scroll: 1, time: 1 },
    VISIT_GAP_MS: 30 * 60 * 1000,
    ACTIVE_STEP_MS: 30 * 1000, // active time worth one 'time' point
    EXPLORE_MAX: 2,            // 'explore' points one page can give
    HALFWAY: 50,               // % of a page that earns its 'scroll' point
    PAUSE_MS: 2000,            // a scroll pause this long is a calm moment

    needed: function (cfg) {
      return Math.max(1, Number(cfg.scoreNeeded) || 4);
    },

    // The stored visit if it is still alive, else a new one.
    resumeVisit: function (prev, now) {
      var alive = prev && typeof prev === 'object' && Number(prev.score) >= 0 &&
        now - Number(prev.last) < PromoPopupRules.VISIT_GAP_MS;
      if (!alive) return { score: 0, points: {}, pages: 0, active: 0, last: now };
      var points = {};
      for (var k in prev.points) if (Number(prev.points[k]) > 0) points[k] = Number(prev.points[k]);
      return { score: Number(prev.score), points: points, pages: Number(prev.pages) || 0,
        active: Number(prev.active) || 0, last: now };
    },

    addPoint: function (visit, kind, now) {
      var points = {};
      for (var k in visit.points) points[k] = visit.points[k];
      points[kind] = (points[kind] || 0) + 1;
      return { score: visit.score + (PromoPopupRules.POINTS[kind] || 0), points: points, pages: visit.pages,
        active: visit.active, last: now };
    },

    // A page load. Landing is not interest, so the first page earns nothing
    // unless it is a product page.
    arrive: function (prev, isProduct, now) {
      var visit = PromoPopupRules.resumeVisit(prev, now);
      var kind = isProduct ? 'product' : (visit.pages > 0 ? 'page' : null);
      if (kind) visit = PromoPopupRules.addPoint(visit, kind, now);
      visit.pages += 1;
      return visit;
    },

    // ms of active time; every ACTIVE_STEP_MS of it is a 'time' point.
    addActive: function (visit, ms, now) {
      var active = visit.active + Math.max(0, ms);
      var v = { score: visit.score, points: visit.points, pages: visit.pages, active: active, last: now };
      while (v.active >= PromoPopupRules.ACTIVE_STEP_MS) {
        var rest = v.active - PromoPopupRules.ACTIVE_STEP_MS;
        v = PromoPopupRules.addPoint(v, 'time', now);
        v.active = rest;
      }
      return v;
    },

    ready: function (cfg, visit) {
      return visit.score >= PromoPopupRules.needed(cfg);
    },

    // page: { scrollY, viewport, height }. A page too short to scroll counts
    // as read.
    scrolledPercent: function (page) {
      var room = page.height - page.viewport;
      if (room <= 0) return 100;
      return Math.min(100, (page.scrollY / room) * 100);
    },

    // m: { now, scrolled, lastScroll, exitIntent, blocked, visible }
    calmMoment: function (m) {
      if (m.blocked || !m.visible) return false;
      if (m.exitIntent) return true;
      return Boolean(m.scrolled) && m.now - m.lastScroll >= PromoPopupRules.PAUSE_MS;
    },

    shouldOpen: function (cfg, state, geo) {
      return PromoPopupRules.eligible(cfg, state) && PromoPopupRules.matchesGeo(cfg.target, geo);
    },

    // What a newsletter POST came back with -> 'success' | 'error' | 'fallback'.
    // r: { status, url, challenge, success, errors } — success/errors are
    // whether our own form in the returned page shows that message.
    classifyResponse: function (r) {
      if (r.challenge || /\/challenge/.test(r.url || '')) return 'fallback';
      if (r.status >= 200 && r.status < 300) {
        if (/customer_posted=true/.test(r.url || '') || r.success) return 'success';
        if (r.errors) return 'error';
      }
      return 'fallback';
    },

    // Where to send a visitor after a successful sign-up, or null to stay.
    // Never the page they are already on (a reload would just loop back here),
    // unless it points at an anchor on it: that only scrolls, it never reloads.
    redirectTarget: function (url, here) {
      url = String(url || '').trim();
      if (!url) return null;
      var to;
      try { to = new URL(url, here); } catch (e) { return null; }
      if (!/^https?:$/.test(to.protocol)) return null;
      var from = new URL(here);
      var samePage = to.origin === from.origin && to.pathname === from.pathname && to.search === from.search;
      if (samePage && (!to.hash || to.hash === from.hash)) return null;
      return to.href;
    }
  };
  // </promo-popup-rules>

  window.PromoPopupRules = PromoPopupRules;

  var SUBSCRIBED_KEY = 'cm-newsletter-subscribed';
  var VISIT_KEY = 'cm-promo-visit';
  var MOBILE_QUERY = '(max-width: 767.98px)';
  var openPopup = null;
  var waiting = []; // popups that may open once the visit is ready

  var store = {
    get: function (k) { try { return window.localStorage.getItem(k); } catch (e) { return null; } },
    set: function (k, v) { try { window.localStorage.setItem(k, v); } catch (e) {} }
  };

  var dismissKey = function (cfg) { return 'cm-promo-popup:' + cfg.storageKey; };

  var readConfig = function (root) {
    var el = root.querySelector('[data-promo-popup-config]');
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  };

  var geoVerdict = function () {
    return Promise.resolve(window.userGeoReady)
      .catch(function () {})
      .then(function () {
        if (!window.geoResolved) return null;
        return { green: window.isGreenState === true, greenB2B: window.isGreenStateB2B === true };
      });
  };

  // ---- the visit: score and this page's signals ----
  var visit = null;
  var page = { isProduct: false, scrolled: false, lastScroll: 0, halfway: false, explore: 0, calculator: false,
    exitIntent: false, lastActivity: 0, lastTick: 0 };
  var ACTIVE_WINDOW_MS = 5000; // input this recent means they are still here
  var TICK_MS = 500;

  var loadVisit = function () {
    try { return JSON.parse(store.get(VISIT_KEY)); } catch (e) { return null; }
  };
  var resetting = false;
  var saveVisit = function () { if (!resetting) store.set(VISIT_KEY, JSON.stringify(visit)); };

  // Read before writing: another tab of the same visit may have scored too.
  var earn = function (kind) {
    var now = Date.now();
    var stored = PromoPopupRules.resumeVisit(loadVisit(), now);
    if (stored.score < visit.score) stored = visit;
    visit = PromoPopupRules.addPoint(stored, kind, now);
    saveVisit();
    checkWaiting();
  };

  var pagePosition = function () {
    var doc = document.documentElement;
    var body = document.body || {};
    return {
      scrollY: window.pageYOffset || doc.scrollTop || 0,
      viewport: window.innerHeight || doc.clientHeight || 0,
      height: Math.max(doc.scrollHeight || 0, body.scrollHeight || 0)
    };
  };

  // Something else on screen the popup must not cover or interrupt.
  var BLOCKERS = '.offcanvas.show, .offcanvas.showing, .modal.show, .trigger-popup.show, [aria-modal="true"]';
  // Fields someone types or picks in. A clicked swatch (radio) keeps focus
  // too, but nothing is being typed there.
  var TYPING = 'input:not([type="radio"]):not([type="checkbox"]):not([type="button"]):not([type="submit"])' +
    ':not([type="range"]):not([type="hidden"]), textarea, select, [contenteditable="true"]';
  var blockedBy = function () {
    if (document.body && document.body.classList.contains('navbar-dropdown-open')) return 'menu open';
    var a = document.activeElement;
    if (a && a.matches && a.matches(TYPING) &&
      !(a.closest && a.closest('[data-promo-popup]'))) return 'typing in a field';
    var open = Array.prototype.filter.call(document.querySelectorAll(BLOCKERS), function (el) {
      if (el.closest('[data-promo-popup]')) return false;
      if (!el.getClientRects().length) return false;
      var cs = window.getComputedStyle(el);
      return cs.visibility !== 'hidden' && cs.opacity !== '0';
    });
    return open.length ? 'another dialog open' : null;
  };

  var moment = function () {
    return {
      now: Date.now(),
      scrolled: page.scrolled,
      lastScroll: page.lastScroll,
      exitIntent: page.exitIntent,
      blocked: Boolean(blockedBy()),
      visible: document.visibilityState !== 'hidden'
    };
  };

  var checkWaiting = function () {
    if (!waiting.length || !visit || !PromoPopupRules.calmMoment(moment())) {
      page.exitIntent = false;
      return;
    }
    page.exitIntent = false;
    waiting = waiting.filter(function (root) {
      if (!PromoPopupRules.ready(root._cfg, visit)) return true;
      root._status = 'shown';
      show(root);
      return false;
    });
  };

  var activity = function () { page.lastActivity = Date.now(); };

  // Counted once per page load, for every popup on the page. Without storage
  // every page is the first one, and only on-page interest can open it.
  var track = function (isProduct) {
    var now = Date.now();
    page.isProduct = isProduct;
    page.lastActivity = now;
    page.lastTick = now;
    visit = PromoPopupRules.arrive(loadVisit(), isProduct, now);
    saveVisit();

    window.addEventListener('scroll', function () {
      var t = Date.now();
      page.scrolled = true;
      page.lastScroll = t;
      page.lastActivity = t;
      if (!page.halfway && PromoPopupRules.scrolledPercent(pagePosition()) >= PromoPopupRules.HALFWAY) {
        page.halfway = true;
        earn('scroll');
      }
    }, { passive: true });

    ['mousemove', 'keydown', 'touchstart', 'click', 'wheel'].forEach(function (type) {
      document.addEventListener(type, activity, { passive: true, capture: true });
    });

    // Desktop exit intent: the pointer leaves through the top of the window.
    document.addEventListener('mouseout', function (e) {
      if (e.relatedTarget || e.clientY > 0) return;
      page.exitIntent = true;
      checkWaiting();
    });

    if (isProduct) {
      var CALCULATOR = '#sf, #lf';
      var explore = function (e) {
        // Only the visitor's own input: the product page clicks and changes
        // its swatches itself while it loads.
        if (!e.isTrusted) return;
        var el = e.target;
        if (!el || !el.closest || el.closest('[data-promo-popup], .offcanvas, header')) return;
        if (e.type === 'change' && !el.matches('input[type="radio"], select')) return;
        if (e.type === 'click' && !el.closest('.slick-slider, .slick-arrow, .slick-dots, [data-fancybox], [class*="gallery"]')) return;
        if (page.explore >= PromoPopupRules.EXPLORE_MAX) return;
        page.explore += 1;
        earn('explore');
      };
      document.addEventListener('change', explore, true);
      document.addEventListener('click', explore, true);

      // An area typed into the materials calculator (Square / Linear Ft.
      // Needed): the strongest sign of a real project. Once per page, counted
      // on change, i.e. when they are done typing.
      document.addEventListener('change', function (e) {
        var el = e.target;
        if (!e.isTrusted || page.calculator || !el || !el.matches || !el.matches(CALCULATOR)) return;
        if (!(Number(el.value) > 0)) return;
        page.calculator = true;
        earn('calculator');
      }, true);
    }

    setInterval(function () {
      var t = Date.now();
      var elapsed = t - page.lastTick;
      page.lastTick = t;
      var active = document.visibilityState !== 'hidden' && t - page.lastActivity < ACTIVE_WINDOW_MS;
      if (active && elapsed > 0 && elapsed < 5 * TICK_MS) {
        visit = PromoPopupRules.addActive(visit, elapsed, t);
        saveVisit();
      }
      checkWaiting();
    }, TICK_MS);

  };

  var show = function (root) {
    if (openPopup && openPopup !== root) return; // one popup at a time
    openPopup = root;
    root._lastFocus = document.activeElement;
    root.hidden = false;
    document.documentElement.classList.add('promo-popup-open');
    requestAnimationFrame(function () {
      root.classList.add('is-open');
      var target = root.querySelector('.promo-popup__input') || root.querySelector('.promo-popup__dialog');
      if (target) target.focus({ preventScroll: true });
    });
  };

  var hide = function (root, remember) {
    if (root.hidden) return;
    root.classList.remove('is-open');
    root.hidden = true;
    if (openPopup === root) openPopup = null;
    document.documentElement.classList.remove('promo-popup-open');
    if (remember && root._cfg) store.set(dismissKey(root._cfg), String(Date.now()));
    if (root._lastFocus && root._lastFocus.focus) root._lastFocus.focus({ preventScroll: true });
  };

  var setMessage = function (root, kind) {
    var ok = root.querySelector('[data-promo-popup-success]');
    var err = root.querySelector('[data-promo-popup-error]');
    var fields = root.querySelector('[data-promo-popup-fields]');
    // kind null clears both messages while a request is in flight.
    if (ok) ok.hidden = kind !== 'success';
    if (err) err.hidden = kind !== 'error';
    if (fields && kind === 'success') fields.hidden = true;
  };

  // After a successful sign-up, go to the configured page (if any), leaving the
  // success message up for the configured delay first.
  var redirectAfterSuccess = function (root) {
    var cfg = root._cfg || {};
    var to = PromoPopupRules.redirectTarget(cfg.redirectUrl, window.location.href);
    if (!to) return;
    // An anchor on this same page only scrolls, so get the popup out of the way.
    var stays = to.split('#')[0] === window.location.href.split('#')[0];
    setTimeout(function () {
      window.location.assign(to);
      if (stays) hide(root, false);
    }, Math.max(0, Number(cfg.redirectDelayMs) || 0));
  };

  var CAPTCHA_BOUND = 'data-hcaptcha-bound';
  var nativeSubmit = function (form) { HTMLFormElement.prototype.submit.call(form); };

  var setBusy = function (form, busy) {
    var button = form.querySelector('[data-promo-popup-submit]');
    if (!button) return;
    button.disabled = busy;
    if (busy) button.setAttribute('aria-busy', 'true'); else button.removeAttribute('aria-busy');
  };

  // Newsletter sign-up, in place, through Shopify's own captcha.
  //
  // Shopify protects customer forms with an invisible hCaptcha
  // (storefront-forms-hcaptcha): it keeps the form's submit() it finds, puts
  // its own in its place and listens for "submit". On submit it gets a token,
  // appends it as h-captcha-response, then calls the submit() it kept. A POST
  // without that token is refused ("Missing CAPTCHA token").
  //
  // So we never post on our own while the captcha is there. We replace
  // form.submit with sendInPlace before Shopify binds (this file runs first),
  // and Shopify ends up calling it with the token already in the form. If
  // Shopify happened to bind first, it calls the native submit instead: a
  // normal page post, and the page comes back with the popup open on the result.
  //
  // Only a clear answer is shown in place: the success redirect, or our form
  // back with validation errors. Anything else goes back through Shopify's
  // flow as a full-page post with a fresh token — never an error for a
  // subscription that may well have gone through.
  var sendInPlace = function (root, form) {
    if (form._native) { nativeSubmit(form); return; }
    form._posting = true;

    var done = function (kind) {
      form._sending = false;
      form._posting = false;
      if (kind === 'fallback') { submitNative(root, form); return; }
      setBusy(form, false);
      if (kind === 'success') store.set(SUBSCRIBED_KEY, String(Date.now()));
      setMessage(root, kind);
      if (kind === 'success') redirectAfterSuccess(root);
    };

    fetch(form.action, { method: 'POST', body: new FormData(form), credentials: 'same-origin' })
      .then(function (res) {
        return res.text().then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var same = doc.getElementById(form.id);
          done(PromoPopupRules.classifyResponse({
            status: res.status,
            url: res.url,
            challenge: /_cf_chl_opt|Missing CAPTCHA/i.test(html),
            success: Boolean(same && same.querySelector('[data-promo-popup-success]:not([hidden])')),
            errors: Boolean(same && same.querySelector('[data-promo-popup-error]:not([hidden])'))
          }));
        });
      })
      .catch(function () { done('fallback'); });
  };

  // Full-page post, the way the footer form does it. With the captcha bound we
  // resubmit through it (a used token is dropped so Shopify issues a new one);
  // without it, a plain submit.
  var submitNative = function (root, form) {
    store.set('cm-promo-popup-return', root.dataset.sectionId);
    form._native = true;
    if (form.getAttribute(CAPTCHA_BOUND) && form.requestSubmit) {
      var used = form.elements['h-captcha-response'];
      if (used) used.remove();
      form.requestSubmit();
    } else {
      nativeSubmit(form);
    }
  };

  var bindForm = function (root, form) {
    if (!form.getAttribute(CAPTCHA_BOUND)) {
      try { form.submit = function () { sendInPlace(root, form); }; } catch (e) {}
    }

    form.addEventListener('submit', function (e) {
      if (form._native) {
        // Our own full-page resubmit: let Shopify's captcha (or the browser) run.
        if (!form.getAttribute(CAPTCHA_BOUND)) return;
        e.preventDefault();
        return;
      }
      e.preventDefault();
      if (form._sending) return;
      form._sending = true;
      setMessage(root, null);
      setBusy(form, true);
      // With the captcha bound, Shopify's own listener is handling this same
      // event: it fetches a token and then calls form.submit -> sendInPlace.
      if (!form.getAttribute(CAPTCHA_BOUND)) { sendInPlace(root, form); return; }
      // If the captcha never answers (its challenge was closed, its script
      // blocked), give the button back so they can try again. No message: the
      // visitor may simply still be solving a challenge.
      clearTimeout(form._watchdog);
      form._watchdog = setTimeout(function () {
        if (!form._sending || form._posting) return;
        form._sending = false;
        setBusy(form, false);
      }, 20000);
    });
  };

  var bind = function (root) {
    if (root._bound) return;
    root._bound = true;

    root.addEventListener('click', function (e) {
      if (e.target.closest('[data-promo-popup-close]')) hide(root, true);
      if (e.target.closest('[data-promo-popup-cta]')) store.set(dismissKey(root._cfg || {}), String(Date.now()));
    });

    root.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { hide(root, true); return; }
      if (e.key !== 'Tab') return;
      // Keep focus inside the dialog.
      var f = root.querySelectorAll('button:not([disabled]), a[href], input:not([type="hidden"]):not([disabled])');
      var visible = Array.prototype.filter.call(f, function (el) { return el.offsetParent !== null; });
      if (!visible.length) return;
      var first = visible[0];
      var last = visible[visible.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });

    var form = root.querySelector('.promo-popup__form');
    if (form) bindForm(root, form);
  };

  var init = function (root) {
    var cfg = readConfig(root);
    if (!cfg) return;
    root._cfg = cfg;

    // The section renders inside <main>, whose stacking context would leave the
    // header above the backdrop. Move the popup to <body>, dropping any copy an
    // earlier render of the same section left there (theme editor reloads).
    if (root.parentNode !== document.body) {
      document.querySelectorAll('body > [data-promo-popup]').forEach(function (old) {
        if (old.dataset.sectionId === root.dataset.sectionId) old.remove();
      });
      document.body.appendChild(root);
    }
    bind(root);

    // In the theme editor the rules are ignored: the popup opens when its
    // section is selected, so it can be edited.
    if (window.Shopify && window.Shopify.designMode) return;

    // Back from a full-page submit (see submitNewsletter): the page rendered
    // the outcome server-side, so open on it. A success without our flag means
    // the footer form was the one posted — they are subscribed, stay closed.
    var returning = store.get('cm-promo-popup-return') === root.dataset.sectionId;
    var success = root.querySelector('[data-promo-popup-success]:not([hidden])');
    var failed = root.querySelector('[data-promo-popup-error]:not([hidden])');
    if (returning) store.set('cm-promo-popup-return', '');
    if (success) store.set(SUBSCRIBED_KEY, String(Date.now()));
    if (returning && (success || failed)) {
      show(root);
      if (success) redirectAfterSuccess(root);
      return;
    }
    if (success) return;

    var state = {
      dismissedAt: Number(store.get(dismissKey(cfg))) || 0,
      subscribed: Boolean(store.get(SUBSCRIBED_KEY)),
      isMobile: window.matchMedia(MOBILE_QUERY).matches,
      now: Date.now()
    };
    var why = PromoPopupRules.whyNot(cfg, state);
    if (why) { root._status = why; return; }
    if (cfg.excluded) { root._status = 'never on this page (cart, search, account, Trade Pro)'; return; }

    root._status = 'waiting for the geo answer';
    var geo = cfg.target === 'all' ? Promise.resolve(null) : geoVerdict();
    geo.then(function (verdict) {
      if (!PromoPopupRules.matchesGeo(cfg.target, verdict)) {
        root._status = 'location is not in the "' + cfg.target + '" target';
        return;
      }
      root._status = 'waiting';
      waiting.push(root);
      checkWaiting();
    });
  };

  var initAll = function () {
    var popups = document.querySelectorAll('[data-promo-popup]');
    if (!(window.Shopify && window.Shopify.designMode)) {
      var cfg = popups.length ? readConfig(popups[0]) : null;
      track(Boolean(cfg && cfg.isProduct));
    }
    popups.forEach(init);
  };

  // For QA: promoReset() forgets this browser's visit, closes, sign-up and
  // reloads, as if it were a first visit. Nothing is written after it runs.
  window.promoReset = function () {
    resetting = true;
    var keys = [VISIT_KEY, SUBSCRIBED_KEY, 'cm-promo-popup-return'];
    document.querySelectorAll('[data-promo-popup]').forEach(function (root) {
      if (root._cfg) keys.push(dismissKey(root._cfg));
    });
    keys.forEach(function (k) { try { window.localStorage.removeItem(k); } catch (e) {} });
    window.location.reload();
  };

  // Where this visitor stands, for QA: promoDebug() in the console.
  window.promoDebug = function () {
    var v = visit || PromoPopupRules.resumeVisit(loadVisit(), Date.now());
    var block = blockedBy();
    var since = page.scrolled ? Math.round((Date.now() - page.lastScroll) / 100) / 10 + ' s ago' : 'not yet on this page';
    var rows = Array.prototype.map.call(document.querySelectorAll('[data-promo-popup]'), function (root) {
      var cfg = root._cfg || {};
      var status = root._status || 'not initialised';
      if (status === 'waiting') {
        var need = PromoPopupRules.needed(cfg);
        status = v.score < need
          ? 'score ' + v.score + '/' + need + ', ' + (need - v.score) + ' to go'
          : 'ready, waiting for a calm moment' + (block ? ' (' + block + ')' : ': stop scrolling 2 s, or move the mouse out the top');
      }
      return { popup: cfg.storageKey, status: status };
    });
    var points = Object.keys(v.points).map(function (k) {
      return k + ' ' + v.points[k] + '×' + PromoPopupRules.POINTS[k];
    }).join(', ') || 'none';
    console.log('[promo] visit score ' + v.score + ' — ' + points + '; ' + v.pages + ' page(s); next time point in ' +
      Math.ceil((PromoPopupRules.ACTIVE_STEP_MS - v.active) / 1000) + ' s of activity');
    console.log('[promo] this page: halfway ' + (page.halfway ? 'yes' : 'no') + ', explored ' + page.explore + '/' +
      PromoPopupRules.EXPLORE_MAX + ', last scroll ' + since);
    rows.forEach(function (r) { console.log('[promo] ' + r.popup + ': ' + r.status); });
    return { visit: v, popups: rows };
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();

  // Theme editor.
  var inSection = function (e) {
    return document.querySelector('#shopify-section-' + e.detail.sectionId + ' [data-promo-popup]') ||
      document.querySelector('body > [data-promo-popup][data-section-id="' + e.detail.sectionId + '"]');
  };
  document.addEventListener('shopify:section:load', function (e) {
    var root = inSection(e);
    if (!root) return;
    init(root);
    show(root);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    var root = inSection(e);
    if (!root) return;
    hide(root, false);
    root.remove();
  });
  document.addEventListener('shopify:section:select', function (e) {
    var root = inSection(e);
    if (root) show(root);
  });
  document.addEventListener('shopify:section:deselect', function (e) {
    var root = inSection(e);
    if (root) hide(root, false);
  });
})();
